import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAnswerFormat } from "./enums/enumAnswerFormat.generated";
import { EnumSalesVisitAssociatedEntityType } from "./enums/enumSalesVisitAssociatedEntityType.generated";

// Model Objects

export class SalesQuestion extends ModelObject<SalesQuestion> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    public questionText: string = "";
    /**
    * Text / Number / YesNo / etc.
    */
    public answerFormat: EnumAnswerFormat = EnumAnswerFormat.none;
    private _outletsSequence: number | null = null;
    /**
    * Ordering Sequence for Outlets (if applicable to Outlets).
    */
    public get outletsSequence(): number | null { return this._outletsSequence }
    public set outletsSequence(value: number | null) {
      this._outletsSequence = value === undefined ? null : value;
    }
    private _groupsSequence: number | null = null;
    /**
    * Ordering Sequence for Groups (if applicable to Groups).
    */
    public get groupsSequence(): number | null { return this._groupsSequence }
    public set groupsSequence(value: number | null) {
      this._groupsSequence = value === undefined ? null : value;
    }
    private _leadsSequence: number | null = null;
    /**
    * Ordering Sequence for Leads (if applicable to Leads).
    */
    public get leadsSequence(): number | null { return this._leadsSequence }
    public set leadsSequence(value: number | null) {
      this._leadsSequence = value === undefined ? null : value;
    }
    private _adHocSequence: number | null = null;
    /**
    * Ordering Sequence for AdHoc visits (if applicable to AdHoc visits).
    */
    public get adHocSequence(): number | null { return this._adHocSequence }
    public set adHocSequence(value: number | null) {
      this._adHocSequence = value === undefined ? null : value;
    }
    public options: (string)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.questionText = jsonSerializedObject.questionText;
          this.answerFormat = EnumAnswerFormat.getByValue(jsonSerializedObject.answerFormat, this.answerFormat);
          this.outletsSequence = jsonSerializedObject.outletsSequence == null ? null : jsonSerializedObject.outletsSequence;
          this.groupsSequence = jsonSerializedObject.groupsSequence == null ? null : jsonSerializedObject.groupsSequence;
          this.leadsSequence = jsonSerializedObject.leadsSequence == null ? null : jsonSerializedObject.leadsSequence;
          this.adHocSequence = jsonSerializedObject.adHocSequence == null ? null : jsonSerializedObject.adHocSequence;
          this.options = jsonSerializedObject.options.map((v: any) => v);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.questionText = this.questionText;
        toRet.answerFormat = this.answerFormat.value;
        toRet.outletsSequence = this.outletsSequence == null ? null : this.outletsSequence;
        toRet.groupsSequence = this.groupsSequence == null ? null : this.groupsSequence;
        toRet.leadsSequence = this.leadsSequence == null ? null : this.leadsSequence;
        toRet.adHocSequence = this.adHocSequence == null ? null : this.adHocSequence;
        toRet.options = this.options.map(v => v);
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "SalesQuestion.Id cannot be empty"},
            {field: "questionText", rule: (v: any) => v == null || v.length <= 1000 || "QuestionText has too many characters (1000)"},
            {field: "outletsSequence", rule: (v: any) => v == null || v >= -2147483648 || "OutletsSequence is not greater than minimum allowed value: -2147483648"},
            {field: "outletsSequence", rule: (v: any) => v == null || v <= 2147483647 || "OutletsSequence is above maximum allowed value: 2147483647"},
            {field: "groupsSequence", rule: (v: any) => v == null || v >= -2147483648 || "GroupsSequence is not greater than minimum allowed value: -2147483648"},
            {field: "groupsSequence", rule: (v: any) => v == null || v <= 2147483647 || "GroupsSequence is above maximum allowed value: 2147483647"},
            {field: "leadsSequence", rule: (v: any) => v == null || v >= -2147483648 || "LeadsSequence is not greater than minimum allowed value: -2147483648"},
            {field: "leadsSequence", rule: (v: any) => v == null || v <= 2147483647 || "LeadsSequence is above maximum allowed value: 2147483647"},
            {field: "adHocSequence", rule: (v: any) => v == null || v >= -2147483648 || "AdHocSequence is not greater than minimum allowed value: -2147483648"},
            {field: "adHocSequence", rule: (v: any) => v == null || v <= 2147483647 || "AdHocSequence is above maximum allowed value: 2147483647"},
    ];
}


export class SalesQuestionService extends ServiceBase {

  public async getAllSalesQuestions(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(SalesQuestion)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.salesQuestions.map((v: any) => new SalesQuestion(v));;
    return this.makeJsonRequest("salesQuestion/v1/getAllSalesQuestions", req, false, extractor, opts);
  };

  public async saveSalesQuestion(salesQuestion: SalesQuestion, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    salesQuestion:salesQuestion.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("salesQuestion/v1/saveSalesQuestion", req, true, extractor, opts);
  };

  public async updateSalesQuestionSequence(entityType: EnumSalesVisitAssociatedEntityType, questionIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    entityType:entityType.value,
    questionIds:questionIds.map(v => v.toString()),
    };
    const extractor = null;
    return this.makeJsonRequest("salesQuestion/v1/updateSalesQuestionSequence", req, true, extractor, opts);
  };
}

export const salesQuestionService = new SalesQuestionService();